import { useTranslation } from "react-i18next";
import { DocsToc } from "../components";
import { DocsTocItem } from "../interfaces";
import {
  DEFAULT_PAGE_LIMIT,
  DEFAULT_TOKEN_TTL_SECONDS,
  MAX_DATE_RANGE_DAYS,
  MAX_PAGE_LIMIT,
} from "../utils";

interface LimitSection {
  key: string;
  values: Record<string, number>;
}

// YOSHI-10464: the numbers travel as interpolation values so the contract states them once and both
// catalogs read the same figure. The gateway throttling section carries none on purpose: that layer
// sits in front of the service and is provisioned outside this repository, so quoting figures here
// would promise an integrator something no code in it guarantees.
const SECTIONS: LimitSection[] = [
  { key: "rate", values: {} },
  { key: "credential", values: {} },
  { key: "tokenRoute", values: { seconds: DEFAULT_TOKEN_TTL_SECONDS } },
  {
    key: "pagination",
    values: { limit: DEFAULT_PAGE_LIMIT, maxLimit: MAX_PAGE_LIMIT },
  },
  { key: "dateRange", values: { days: MAX_DATE_RANGE_DAYS } },
  { key: "ipAllowList", values: {} },
];

const TOC_ITEMS: DocsTocItem[] = [
  ...SECTIONS.map((section): DocsTocItem => {
    return {
      id: `limits-${section.key}`,
      translationKey: `docs.limits.sections.${section.key}.title`,
      level: 1,
    };
  }),
  {
    id: "limits-advice",
    translationKey: "docs.limits.advice.title",
    level: 1,
  },
];

export const Limits = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col-reverse gap-8 lg:flex-row lg:gap-12">
      <div className="min-w-0 flex-1 space-y-12">
        <div className="space-y-4">
          <h2 className="text-xl font-bold lg:text-2xl">
            {t("docs.limits.title")}
          </h2>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.limits.intro")}
          </p>
        </div>

        {SECTIONS.map((section) => (
          <div key={section.key} className="space-y-4">
            <h3
              id={`limits-${section.key}`}
              className="scroll-mt-6 text-lg font-bold lg:text-xl"
            >
              {t(`docs.limits.sections.${section.key}.title`)}
            </h3>
            <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
              {t(`docs.limits.sections.${section.key}.content`, section.values)}
            </p>
          </div>
        ))}

        <div
          id="limits-advice"
          className="scroll-mt-6 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-5"
        >
          <h3 className="text-base font-bold">{t("docs.limits.advice.title")}</h3>
          <ul className="list-outside list-disc space-y-2 pl-5 text-gray-400">
            <li className="text-base/relaxed">
              {t("docs.limits.advice.cacheToken")}
            </li>
            {/* YOSHI-10464: the API applies the per credential limit, so an integrator has to know
                that a 429 carries a Retry-After and that honouring it is the whole recovery. */}
            <li className="text-base/relaxed">
              {t("docs.limits.advice.honourRetryAfter")}
            </li>
            <li className="text-base/relaxed">
              {t("docs.limits.advice.useTotals")}
            </li>
            <li className="text-base/relaxed">
              {t("docs.limits.advice.splitRange", {
                days: MAX_DATE_RANGE_DAYS,
              })}
            </li>
          </ul>
        </div>
      </div>
      <DocsToc items={TOC_ITEMS} />
    </div>
  );
};
